import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { Badge, Button, Card, PageHeader, Section } from '@/components/ui';
import { DeploymentTimeline } from '@/components/charts';
import { SITE } from '@/lib/site';

const forms = [
  { name: 'SaaS subscription', note: 'Monthly or annual', who: 'Teams that want the stack without running GPUs.', points: ['The Fnk stack, operated by Fnk', 'Framework, control layer and inference engine as a managed service', 'No hardware to buy or operate', 'Support with response objectives'], accent: true },
  { name: 'Stack licence', note: 'Year one, then recurring', who: 'Inference providers, data centres, enterprise GPU estates, OEMs.', points: ['Framework, control layer and inference engine', 'On the GPUs you already run', 'Harness priced in the negotiation', 'Beneath the NVIDIA AI Enterprise line on every hardware class'] },
  { name: 'Full box', note: 'Year one, then recurring', who: 'Regulated organisations without a platform team.', points: ['Stack, Harness, connectors and console', 'Licensed server hardware, preinstalled', 'Air-gapped where required', 'One vendor accountable for the whole system'] },
];

const compare = [
  ['Build it in house', 'A system only their team can operate'],
  ['Enterprise knowledge-AI SaaS', 'Data leaves the environment'],
  ['Hyperscaler private AI stack + integrator', 'Multiple vendors, no single accountability'],
  ['Partial box', 'Complete system, data stays, one vendor'],
  ['Full box', 'The above, plus hardware, one purchase order'],
];

const faqs = [
  ['How is the licence priced?', 'Per GPU, per year. It does not depend on a measured result or on how many GPUs you save.'],
  ['What is the difference between the two boxes?', 'Nothing in the software. The full box ships on licensed hardware, preinstalled; the partial box installs the same build on hardware you already own. Choose by who owns the metal.'],
  ['Does the Harness cost extra?', 'It ships inside every box. For a stack-licence buyer it is part of the negotiation, because a buyer who already has the application may need less of it.'],
  ['What is in the recurring fee?', 'The licence, connector maintenance and support with response objectives. Integration and hardware are year-one costs only.'],
];

export function PricingPage() {
  return (
    <div>
      <PageHeader
        kicker="Pricing"
        title={<span>The licence is priced per GPU.</span>}
        lead="Per GPU per year, one list price per hardware class — the same for every buyer, set before you sign and independent of any measured result."
        actions={
          <a href={SITE.salesMailto} className="w-full sm:w-auto">
            <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Contact sales</Button>
          </a>
        }
      />

      <Section n="01" kicker="Three ways to engage" title="Rent it, license it, or take the whole box." tone="white">
        <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-3">
          {forms.map((f) => (
            <Card key={f.name} variant={f.accent ? 'accent' : 'raised'} className="flex h-full flex-col p-5 sm:p-6">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold tracking-tight">{f.name}</h3>
                {f.accent && <Badge variant="accent">Start here</Badge>}
              </div>
              <p className="mt-2 font-mono text-[11px] tabular text-ink-3">{f.note}</p>
              <p className="mt-3 text-[13px] text-ink-2">{f.who}</p>
              <ul className="mt-4 flex-1 space-y-2">
                {f.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[13px] text-ink-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <a href={SITE.salesMailto} className="mt-5 block">
                <Button variant={f.accent ? 'primary' : 'secondary'} size="sm" className="w-full" rightIcon={<ArrowUpRight className="h-3.5 w-3.5" />}>Contact sales</Button>
              </a>
            </Card>
          ))}
        </div>
      </Section>

      <Section n="02" kicker="What the customer compares it to" title="The alternatives, side by side." tone="white">
        <Card variant="raised" className="overflow-x-auto p-4 sm:p-5">
          <table className="w-full min-w-[420px] border-collapse text-left">
            <thead>
              <tr className="border-b border-ink">
                <th className="label py-2.5 pr-4 normal-case">Alternative</th>
                <th className="label py-2.5 normal-case">What they get</th>
              </tr>
            </thead>
            <tbody>
              {compare.map((row, i) => (
                <tr key={row[0]} className={`border-b border-line last:border-b-0 ${i >= 3 ? 'bg-accent-soft/40' : ''}`}>
                  <td className={`py-2.5 pr-4 text-[13px] font-medium ${i >= 3 ? 'text-accent-ink' : 'text-ink'}`}>{row[0]}</td>
                  <td className="py-2.5 text-[13px] text-ink-2">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </Section>

      <Section n="03" kicker="How a deployment runs" title="Nine to sixteen weeks from signature to production.">
        <Card variant="raised" className="p-5 sm:p-8">
          <DeploymentTimeline />
        </Card>
      </Section>

      <Section n="04" kicker="Questions" title="Asked before, answered here." tone="white">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {faqs.map(([q, a]) => (
            <Card key={q} variant="plain" className="p-5">
              <h3 className="text-sm font-semibold tracking-tight">{q}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">{a}</p>
            </Card>
          ))}
        </div>
      </Section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile font-semibold tracking-heading md:text-section-desktop">Get the number on your own workload.</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-2">Tell us what you run and on what. We will come back with an assessment scope, a price and a date.</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href={SITE.salesMailto} className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Contact sales</Button>
              </a>
              <Link to="/product" className="w-full sm:w-auto">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>What is in the stack</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
